import type { CellContext, ColumnDef, GroupColumnDef } from '@tanstack/react-table';
import { createColumnHelper } from '@tanstack/react-table';
import type { TitleSize } from '@tempo-io/roadmap-dto';
import { Button } from '@tempo-io/sui-button';
import { cn } from '@tempo-io/sui-class-utils';
import { CollapseContent, Expand } from '@tempo-io/sui-icons';
import { differenceInDays, format } from 'date-fns';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ItemV2 } from '@/lib/api/types/items';
import type { IVisualizationHeaderProps } from '@/lib/api/types/loop-visualization';
import { DEFAULT_BASE_COLOR } from '@/lib/utils/loop-visualization/colors';
import { ITEM_LANE_HEIGHT, ITEM_LANE_HEIGHT_COMPACT } from './TimelineItem';
import { TimelineItemCell } from './TimelineItemCell';
import {
  EmptyRowCell,
  MonthHeader,
  ParentRowHeader,
  QuarterHeader,
  RowHeader,
  VerticalParentRowHeader,
  WeekHeader,
} from './TimelineTableHeaderComponents';
import { PARENT_HEADER_HEIGHT, VERTICAL_ROW_HEADER_WIDTH } from './useTimelineColumnDayWidth';
import { buildTimelineDateAxis, formatDateHeader, getMonthName } from './utils/timelineDateUtils';

export type TimelineItemType = ItemV2 & {
  order?: number;
  progress?: number;
  children?: TimelineItemType[];
  isEditable?: boolean;
  isPlaceholder?: boolean;
};

export type TimelineRowType = IVisualizationHeaderProps & {
  items?: { [key: string]: TimelineItemType[] };
  allItems?: TimelineItemType[];
  maxOrder?: number;
  subRows?: TimelineRowType[];
  mainHeaderLabel?: string;
  mainHeaderColor?: string;
  isFirstSubRow?: boolean;
  firstSubRowId?: string;
  subRowCount?: number;
  totalLanesInGroup?: number;
  longestItemDurationInDays: number;
  parentHeaderSelector?: IVisualizationHeaderProps['selector'];
  headerId: string;
  parentHeaderId?: string;
  calculatedHeight?: number;
};

export type TimelineTableCellContext<TData extends TimelineRowType, TValue> = CellContext<
  TData,
  TValue
> & {
  isRowDraggedOver: boolean;
  isDropForbidden: boolean;
  extraLanes: number;
};

export const columnHelper = createColumnHelper<TimelineRowType>();

const getRowColor = (row: TimelineRowType, hasVerticalSubHeaders: boolean) =>
  (hasVerticalSubHeaders ? row.mainHeaderColor : row.headerColor) || DEFAULT_BASE_COLOR;

export const useTimelineTableColumns = ({
  from,
  to,
  canEdit,
  canResize,
  canDragBetweenHeaders,
  canDragDates,
  isPublishedMode,
  dayWidth,
  isVertical,
  hasSubHeaders,
  hasVerticalSubHeaders,
  showTodayLine,
  titleSize,
  expandedChildren,
  showChildItems,
  setExpandedChildren,
  onItemClick,
  itemColorById,
  onResizeEnd,
  isCompact,
  rowHeaderWidth,
  setReorderHeadersModalState,
  onHideHeader,
  progressFieldId,
  systemProgressMap,
}: {
  from: string;
  to: string;
  canEdit: boolean;
  canResize: boolean;
  canDragBetweenHeaders: boolean;
  canDragDates: boolean;
  isPublishedMode: boolean;
  dayWidth: number;
  isVertical: boolean;
  hasSubHeaders: boolean;
  hasVerticalSubHeaders: boolean;
  showTodayLine: boolean;
  titleSize: TitleSize;
  expandedChildren: Record<string, boolean>;
  showChildItems: boolean;
  setExpandedChildren: Dispatch<
    SetStateAction<{
      [key: number]: boolean;
    }>
  >;
  setReorderHeadersModalState: React.Dispatch<
    React.SetStateAction<{
      isReorderingMainHeader: boolean;
      parentHeaderId: string | null;
    } | null>
  >;
  onHideHeader?: (headerId: string, isMainHeader: boolean, parentHeaderId?: string) => void;
  onItemClick: (itemId: number) => void;
  itemColorById?: Record<number, string>;
  onResizeEnd?: (itemId: number, newStartDate: string, newEndDate: string) => void;
  isCompact?: boolean;
  rowHeaderWidth: number;
  progressFieldId: number | null;
  systemProgressMap: Map<number, number> | null;
}): ColumnDef<TimelineRowType, { isRowDraggedOver?: boolean }>[] => {
  const { t } = useTranslation();
  return useMemo(() => {
    const fixedColumns = [];

    const mainHeaderColumn = columnHelper.accessor('mainHeaderLabel', {
      id: 'main-header',
      header: () => <div className={cn('flex w-full h-full')} />,
      cell: (info) => {
        const row = info.row.original;
        if (!hasVerticalSubHeaders || !row.isFirstSubRow) {
          return null;
        }
        const totalLanesInGroup = row.totalLanesInGroup || 1;
        const marginBuffer = isCompact ? 1 : 4;
        const laneHeight = isCompact ? ITEM_LANE_HEIGHT_COMPACT : ITEM_LANE_HEIGHT;
        // When in compact mode, there is no padding/margin between the rows, but in regular mode there is some.
        const totalHeight = isCompact
          ? totalLanesInGroup * laneHeight + (row.subRowCount || 0) * marginBuffer
          : totalLanesInGroup * laneHeight - marginBuffer;

        const toggleReorderModal =
          !canEdit || isPublishedMode
            ? undefined
            : () => {
                setReorderHeadersModalState({
                  isReorderingMainHeader: true,
                  parentHeaderId: null,
                });
              };

        const hideHeader =
          !canEdit || isPublishedMode || !onHideHeader
            ? undefined
            : () => onHideHeader(row.headerId, true);

        return (
          <VerticalParentRowHeader
            expanded={info.row.getIsExpanded()}
            label={info.getValue()}
            isCompact={isCompact}
            color={row.mainHeaderColor}
            totalHeight={totalHeight}
            onClick={info.row.getToggleExpandedHandler()}
            toggleReorderModal={toggleReorderModal}
            onHideHeader={hideHeader}
          />
        );
      },
      size: hasVerticalSubHeaders ? PARENT_HEADER_HEIGHT : rowHeaderWidth,
    });

    const subHeaderColumn = columnHelper.accessor('label', {
      id: 'row-header',
      header: (info) => {
        if (!hasSubHeaders) {
          return null;
        }
        const areAllExpanded = info.table.getIsAllRowsExpanded();
        const toggleAllHandler = info.table.getToggleAllRowsExpandedHandler();
        return (
          <div className={cn('flex w-full h-full items-end justify-end')}>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-6 m-1 mt-0 rounded-xs text-text-neutral-primary"
              onClick={toggleAllHandler}
            >
              {areAllExpanded ? (
                <>
                  <CollapseContent />
                  {t(($) => $.common.collapseAll)}
                </>
              ) : (
                <>
                  <Expand />
                  {t(($) => $.common.expandAll)}
                </>
              )}
            </Button>
          </div>
        );
      },
      cell: (info) => {
        if (hasVerticalSubHeaders) {
          const firstSubRowInGroup = info.table.getRow(info.row.original.firstSubRowId || '');
          const isSubGroupExpanded = firstSubRowInGroup.getIsExpanded();
          if (!isSubGroupExpanded) {
            return null;
          }

          const toggleReorderModal =
            !canEdit || isPublishedMode
              ? undefined
              : () => {
                  setReorderHeadersModalState({
                    isReorderingMainHeader: false,
                    parentHeaderId: info.row.original.parentHeaderId || null,
                  });
                };

          const hideHeader =
            !canEdit || isPublishedMode || !onHideHeader
              ? undefined
              : () =>
                  onHideHeader(info.row.original.headerId, false, info.row.original.parentHeaderId);

          return (
            <RowHeader
              label={info.getValue()}
              color={info.row.original.headerColor}
              isVertical={false}
              isCompact={isCompact}
              toggleReorderModal={toggleReorderModal}
              onHideHeader={hideHeader}
            />
          );
        }
        const isParent = hasSubHeaders && !info.row.original.parentHeaderId;
        const expanded = info.row.getIsExpanded();
        const toggleExpanded = info.row.getToggleExpandedHandler();

        if (isParent) {
          const toggleReorderModal =
            !canEdit || isPublishedMode
              ? undefined
              : () => {
                  setReorderHeadersModalState({
                    isReorderingMainHeader: true,
                    parentHeaderId: null,
                  });
                };
          const hideHeader =
            !canEdit || isPublishedMode || !onHideHeader
              ? undefined
              : () => onHideHeader(info.row.original.headerId, true);
          return (
            <ParentRowHeader
              expanded={expanded}
              label={info.getValue()}
              color={info.row.original.headerColor}
              hasVerticalSubHeaders={hasVerticalSubHeaders}
              onClick={toggleExpanded}
              toggleReorderModal={toggleReorderModal}
              onHideHeader={hideHeader}
            />
          );
        }

        const isChild = !!info.row.depth;
        const isMainHeader = !hasSubHeaders || !info.row.original.parentHeaderId;
        const toggleReorderModal =
          !canEdit || isPublishedMode
            ? undefined
            : () => {
                setReorderHeadersModalState({
                  isReorderingMainHeader: !hasSubHeaders,
                  parentHeaderId: info.row.original.parentHeaderId || null,
                });
              };
        const hideHeader =
          !canEdit || isPublishedMode || !onHideHeader
            ? undefined
            : () =>
                onHideHeader(
                  info.row.original.headerId,
                  isMainHeader,
                  info.row.original.parentHeaderId,
                );
        return (
          <RowHeader
            isChild={isChild}
            label={info.getValue()}
            color={info.row.original.headerColor}
            isVertical={isVertical}
            isCompact={isCompact}
            toggleReorderModal={toggleReorderModal}
            onHideHeader={hideHeader}
          />
        );
      },
      size: isVertical && !hasVerticalSubHeaders ? VERTICAL_ROW_HEADER_WIDTH : rowHeaderWidth,
    });

    if (hasVerticalSubHeaders) {
      fixedColumns.push(mainHeaderColumn, subHeaderColumn);
    } else {
      fixedColumns.push(subHeaderColumn);
    }

    const fixedColumnGroup = columnHelper.group({
      id: 'fixed',
      header: () => null,
      columns: fixedColumns,
    });

    const dateGroupColumns: GroupColumnDef<TimelineRowType, unknown>[] = [];

    const axis = buildTimelineDateAxis({ iterationFrom: from, iterationTo: to });
    if (!axis) return [fixedColumnGroup];
    const { isMoreThanHalfYear } = axis;

    if (axis.useQuarterView) {
      for (const quarterSlice of axis.quarters) {
        const { year, quarter, quarterLabel, months: monthsToUse } = quarterSlice;

        const monthColumns = monthsToUse.map((month, index) => {
          const accessorKey = `items.${month.key}` as `items.${string}`;
          const monthName = getMonthName(month.start);
          const daysInMonth = differenceInDays(month.end, month.start) + 1;
          const monthWidth = dayWidth * daysInMonth;

          return columnHelper.accessor(accessorKey, {
            id: `${year}-q${quarter}-month-${index}`,
            header: () => <MonthHeader monthName={monthName} isMain={false} />,
            cell: (info) => {
              const { isRowDraggedOver, isDropForbidden, extraLanes, row, getValue } =
                info as TimelineTableCellContext<TimelineRowType, unknown>;
              const original = row.original;
              if (original.subRows?.length) {
                return (
                  <EmptyRowCell
                    isCompact={isCompact}
                    intervalStart={month.start}
                    intervalEnd={month.end}
                    showTodayLine={showTodayLine}
                    dayWidth={dayWidth}
                    headerLeftOffset={0}
                  />
                );
              }

              if (hasVerticalSubHeaders) {
                const firstSubRowInGroup = info.table.getRow(info.row.original.firstSubRowId || '');
                const isSubGroupExpanded = firstSubRowInGroup.getIsExpanded();
                if (!isSubGroupExpanded) {
                  return (
                    <EmptyRowCell
                      isCompact={isCompact}
                      intervalStart={month.start}
                      intervalEnd={month.end}
                      showTodayLine={showTodayLine}
                      dayWidth={dayWidth}
                      headerLeftOffset={0}
                    />
                  );
                }
              }
              const itemsForMonth = (getValue() || []) as TimelineItemType[];
              const rowColor = getRowColor(row.original, hasVerticalSubHeaders);

              const allItemsInRow = row.original.allItems || [];

              return (
                <TimelineItemCell
                  itemsForInterval={itemsForMonth}
                  intervalStart={month.start}
                  intervalEnd={month.end}
                  showTodayLine={showTodayLine}
                  from={from}
                  to={to}
                  canDragBetweenHeaders={canDragBetweenHeaders}
                  canDragDates={canDragDates}
                  canResize={canResize}
                  rowColor={rowColor}
                  itemColorById={itemColorById}
                  dayWidth={dayWidth}
                  titleSize={titleSize}
                  expandedChildren={expandedChildren}
                  showChildItems={showChildItems}
                  setExpandedChildren={setExpandedChildren}
                  onResizeEnd={onResizeEnd}
                  row={row.original}
                  isRowDraggedOver={isRowDraggedOver}
                  isDropForbidden={isDropForbidden}
                  isCompact={isCompact}
                  onItemClick={onItemClick}
                  allItemsInRow={allItemsInRow}
                  headerLeftOffset={0}
                  extraLanes={extraLanes}
                  progressFieldId={progressFieldId}
                  systemProgressMap={systemProgressMap}
                  isPublishedMode={isPublishedMode}
                />
              );
            },
            size: monthWidth,
          });
        });

        dateGroupColumns.push(
          columnHelper.group({
            id: `quarter-${year}-${quarter}`,
            header: () => <QuarterHeader quarterLabel={quarterLabel} />,
            columns: monthColumns,
          }),
        );
      }
    } else {
      for (const monthGroup of axis.months) {
        const {
          year,
          monthIndex: monthNum,
          monthName,
          visibleMonthStart,
          visibleMonthEnd,
          biWeeks: biWeeksToUse,
          isEmptyState,
        } = monthGroup;
        const daysInMonth = differenceInDays(visibleMonthEnd, visibleMonthStart) + 1;
        const monthWidth = dayWidth * daysInMonth;

        const biWeeksForColumns = isEmptyState
          ? [
              {
                start: visibleMonthStart,
                end: visibleMonthEnd,
                key: format(visibleMonthStart, 'yyyy-MM-dd'),
                index: 0,
                headerLeftOffset: 0,
                biWeekWidth: monthWidth,
                equalColWidth: monthWidth,
              },
            ]
          : biWeeksToUse.map((biWeek) => {
              const equalColWidth = monthWidth / biWeeksToUse.length;
              const daysInBiWeek = differenceInDays(biWeek.end, biWeek.start) + 1;
              const biWeekWidth = dayWidth * daysInBiWeek;
              const offsetFromVisibleStart = differenceInDays(biWeek.start, visibleMonthStart);
              const dateBasedLeftOffset = offsetFromVisibleStart * dayWidth;
              const columnLeftOffset = biWeek.index * equalColWidth;
              const headerLeftOffset = dateBasedLeftOffset - columnLeftOffset;

              return {
                start: biWeek.start,
                end: biWeek.end,
                key: biWeek.key,
                index: biWeek.index,
                headerLeftOffset,
                biWeekWidth,
                equalColWidth,
              };
            });

        const biWeekColumns = biWeeksForColumns.map((biWeek) => {
          const accessorKey = `items.${biWeek.key}` as `items.${string}`;

          return columnHelper.accessor(accessorKey, {
            id: isEmptyState
              ? `${year}-${monthNum}-empty`
              : `${year}-${monthNum}-biweek-${biWeek.index}`,
            header: () =>
              isEmptyState ? null : (
                <WeekHeader
                  rangeStart={formatDateHeader(biWeek.start)}
                  rangeEnd={formatDateHeader(biWeek.end)}
                  leftOffset={biWeek.headerLeftOffset}
                  width={biWeek.biWeekWidth}
                  rangeMoreThanHalfYear={isMoreThanHalfYear}
                />
              ),
            cell: (info) => {
              const { isRowDraggedOver, isDropForbidden, extraLanes, row, getValue } =
                info as TimelineTableCellContext<TimelineRowType, unknown>;
              const original = row.original;

              if (original.subRows?.length) {
                return (
                  <EmptyRowCell
                    isCompact={isCompact}
                    intervalStart={biWeek.start}
                    intervalEnd={biWeek.end}
                    showTodayLine={showTodayLine}
                    dayWidth={dayWidth}
                    headerLeftOffset={biWeek.headerLeftOffset}
                  />
                );
              }

              if (hasVerticalSubHeaders) {
                const firstSubRowInGroup = info.table.getRow(info.row.original.firstSubRowId || '');
                const isSubGroupExpanded = firstSubRowInGroup.getIsExpanded();
                if (!isSubGroupExpanded) {
                  return (
                    <EmptyRowCell
                      isCompact={isCompact}
                      intervalStart={biWeek.start}
                      intervalEnd={biWeek.end}
                      showTodayLine={showTodayLine}
                      dayWidth={dayWidth}
                      headerLeftOffset={biWeek.headerLeftOffset}
                    />
                  );
                }
              }

              const itemsForInterval = isEmptyState
                ? []
                : ((getValue() || []) as TimelineItemType[]);
              const rowColor = getRowColor(row.original, hasVerticalSubHeaders);
              const allItemsInRow = row.original.allItems || [];

              return (
                <TimelineItemCell
                  itemsForInterval={itemsForInterval}
                  intervalStart={biWeek.start}
                  intervalEnd={biWeek.end}
                  showTodayLine={showTodayLine}
                  from={from}
                  to={to}
                  canResize={canResize}
                  canDragBetweenHeaders={canDragBetweenHeaders}
                  canDragDates={canDragDates}
                  rowColor={rowColor}
                  itemColorById={itemColorById}
                  dayWidth={dayWidth}
                  titleSize={titleSize}
                  expandedChildren={expandedChildren}
                  showChildItems={showChildItems}
                  setExpandedChildren={setExpandedChildren}
                  onResizeEnd={onResizeEnd}
                  onItemClick={onItemClick}
                  row={row.original}
                  isRowDraggedOver={isRowDraggedOver}
                  isDropForbidden={isDropForbidden}
                  isCompact={isCompact}
                  allItemsInRow={allItemsInRow}
                  headerLeftOffset={biWeek.headerLeftOffset}
                  extraLanes={extraLanes}
                  progressFieldId={progressFieldId}
                  systemProgressMap={systemProgressMap}
                  isPublishedMode={isPublishedMode}
                />
              );
            },
            size: biWeek.equalColWidth,
          });
        });

        dateGroupColumns.push(
          columnHelper.group({
            id: `month-${year}-${monthNum}-${monthName}`,
            header: () => <MonthHeader monthName={monthName} isMain />,
            columns: biWeekColumns,
          }),
        );
      }
    }

    return [fixedColumnGroup, ...dateGroupColumns];
  }, [
    from,
    to,
    onResizeEnd,
    onItemClick,
    isVertical,
    hasSubHeaders,
    hasVerticalSubHeaders,
    dayWidth,
    itemColorById,
    expandedChildren,
    showChildItems,
    setExpandedChildren,
    showTodayLine,
    titleSize,
    isCompact,
    rowHeaderWidth,
    canEdit,
    canResize,
    canDragBetweenHeaders,
    canDragDates,
    isPublishedMode,
    progressFieldId,
    systemProgressMap,
    setReorderHeadersModalState,
    onHideHeader,
    t,
  ]);
};
